import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const NAV_GRADIENT = "linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)";

export default function PoliticaPrivacidad() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="sticky top-0 z-50 border-b border-purple-500/20" style={{ background: NAV_GRADIENT }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/landing")} className="flex items-center gap-2 text-white/90 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-semibold text-sm">Volver</span>
          </button>
          <img src="/logo.png" alt="SkiFlow Rental" className="h-7 w-auto object-contain" />
          <div className="w-16" />
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 sm:p-12">
          <div className="mb-10 pb-8 border-b border-slate-100">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-purple-700 bg-purple-50 mb-4">
              RGPD · Legal
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-3" style={{ fontFamily: "Plus Jakarta Sans" }}>
              Política de Privacidad
            </h1>
            <p className="text-slate-500 text-sm">Última actualización: enero de 2025 · FASCINADS SOLUCIONES SL</p>
          </div>

          <div className="space-y-8 text-slate-600 leading-relaxed text-sm sm:text-base">

            <p>
              Queremos invitarte personalmente a leer esta política en donde obtendrás información completa y transparente sobre cómo se recaban tus datos en estas webs, la finalidad con la que se utiliza esa información, los sistemas de captura así como las herramientas que utilizo para gestionar esa información.
            </p>

            <section>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">Dominios cubiertos por esta política</h2>
              <ul className="space-y-2 pl-4">
                {["https://fascinads.com", "https://skiflowrental.com"].map(url => (
                  <li key={url} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-purple-500 flex-shrink-0" />
                    <a href={url} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">{url}</a>
                  </li>
                ))}
              </ul>
              <p className="mt-4">
                Dichos dominios son responsabilidad de <strong>FASCINADS SOLUCIONES SL</strong> que obra también como responsable de toda la información recabada mediante dichos enlaces.
              </p>
              <p className="mt-3">
                Te animo a leer detenidamente estos términos antes de facilitar tus datos personales en estas webs. Los mayores de trece años podrán registrarse como usuarios sin el previo consentimiento de sus padres o tutores. En el caso de los menores de trece años se requiere el consentimiento de los padres o tutores.
              </p>
            </section>

            <section className="p-5 bg-slate-50 rounded-xl border border-slate-100">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Responsable del tratamiento</h2>
              <dl className="grid sm:grid-cols-2 gap-3 text-sm">
                {[
                  ["Identidad", "FASCINADS SOLUCIONES SL"],
                  ["Nombre comercial", "FASCINADS SOLUCIONES SL / SkiFlow"],
                  ["NIF/CIF", "B70880182"],
                  ["Dirección", "Juez José Bernabeu Rocamora, 31 - 03348 Granja de Rocamora (Alicante)"],
                  ["Correo", "hola@fascinads.com · hola@skiflowrental.com"],
                ].map(([dt, dd]) => (
                  <div key={dt} className="bg-white rounded-lg p-3 border border-slate-100">
                    <dt className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-0.5">{dt}</dt>
                    <dd className="text-slate-700">{dd}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-4">Principios que aplicaremos a tu información</h2>
              <div className="space-y-3">
                {[
                  ["Licitud, lealtad y transparencia", "Siempre vamos a requerir tu consentimiento para el tratamiento de tus datos personales para uno o varios fines específicos que te informaremos previamente con absoluta transparencia."],
                  ["Minimización de datos", "Solo vamos a solicitar datos estrictamente necesarios en relación con los fines para los que requerimos. Los mínimos posibles."],
                  ["Limitación del plazo de conservación", "Los datos serán mantenidos durante no más tiempo del necesario para los fines del tratamiento. En el caso de suscripciones, periódicamente revisaremos nuestras listas y eliminaremos aquellos registros inactivos durante un tiempo considerable."],
                  ["Integridad y confidencialidad", "Tus datos serán tratados de tal manera que se garantice una seguridad adecuada y se garantice confidencialidad. Tomamos todas las precauciones necesarias para evitar el acceso no autorizado o uso indebido de los datos."],
                ].map(([title, desc]) => (
                  <div key={title} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="font-semibold text-slate-800 mb-1">Principio de {title}</p>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">¿Cómo hemos obtenido tus datos?</h2>
              <p className="mb-3">Los datos personales que trato en nuestros dominios proceden de:</p>
              <ul className="grid sm:grid-cols-2 gap-2">
                {["Formulario de contacto","Formulario de subscripción","Formulario de solicitud de servicios","Formulario de descargas","Formulario de alta como afiliado","Formulario de inscripción para webinar","Formularios de páginas de aterrizaje","Comentarios en blog","Formulario de registro","Sistema de chat online"].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-purple-500 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">¿Cuáles son tus derechos?</h2>
              <p className="mb-3">Las personas interesadas tienen derecho a:</p>
              <ul className="space-y-2 pl-4">
                {["Solicitar el acceso a los datos personales relativos al interesado","Solicitar su rectificación o supresión","Solicitar la limitación de su tratamiento","Oponerse al tratamiento","Solicitar la portabilidad de los datos"].map(d => (
                  <li key={d} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-purple-500 flex-shrink-0" />{d}
                  </li>
                ))}
              </ul>
              <p className="mt-4">Para ejercer tus derechos, puedes contactar en: <a href="mailto:hola@fascinads.com" className="text-purple-600 hover:underline">hola@fascinads.com</a> o <a href="mailto:hola@skiflowrental.com" className="text-purple-600 hover:underline">hola@skiflowrental.com</a>.</p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">¿Con qué finalidad tratamos tus datos?</h2>
              <div className="space-y-3">
                {[
                  ["Formulario de contacto y solicitud de servicios", "Solicito datos personales (Nombre y apellidos, Email, nº de teléfono) para responder a los requerimientos de los usuarios. Los datos podrán estar ubicados en servidores dentro o fuera de la UE (ej: Loading, Mailerlite, Google Analytics, Facebook Ads)."],
                  ["Formulario de suscripción y páginas de aterrizaje", "Solicito datos personales para gestionar la lista de suscripciones, enviar boletines, promociones y ofertas especiales. Gestionado por plataformas como Mailerlite, GoHighLevel u otros similares."],
                  ["Formulario de alta para comentarios y registro", "Solicito datos personales para que puedas crearte una cuenta en mis sistemas y tengas acceso a sus funcionalidades."],
                ].map(([title, desc]) => (
                  <div key={title} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="font-semibold text-slate-800 mb-1">{title}</p>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">Remarketing</h2>
              <p>La función de remarketing nos permite llegar a las personas que hayan visitado nuestros dominios anteriormente. Como usuario te informamos que estamos recopilando información para esta función a través de cookies de <strong>Facebook Ads</strong> y <strong>Google Adwords</strong>. Puedes inhabilitar el uso de cookies a través de la Configuración de anuncios de Google o de la Network Advertising Initiative.</p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">Legitimación y categoría de datos</h2>
              <p className="mb-2">La base legal para el tratamiento de sus datos es: el <strong>consentimiento</strong>. Para contactar o realizar comentarios en estas webs se requiere el consentimiento con esta política de privacidad.</p>
              <p>Las categorías de datos que se tratan son: <strong>datos identificativos</strong> (nombre, apellidos, correo electrónico y teléfono). No se tratan categorías de datos especialmente protegidos.</p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">Conservación y destinatarios</h2>
              <p className="mb-3">Los datos personales proporcionados se conservarán hasta que no se solicite su supresión por el interesado.</p>
              <p>Para prestar servicios estrictamente necesarios, FASCINADS SOLUCIONES SL comparte datos con los siguientes prestadores: <strong>Google Analytics</strong>, proveedores de <strong>Hosting</strong>, canales como WhatsApp y Telegram, plataformas de <strong>Email marketing</strong> (Sendinblue, ActiveCampaign) y plataformas web (Automattic Inc). <strong>No se venden, alquilan ni ceden datos personales a terceros sin consentimiento previo.</strong></p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3">Seguridad de los datos</h2>
              <p>Estas webs incluyen un <strong>certificado SSL</strong>. La transmisión de datos entre servidor y usuario está totalmente cifrada. FASCINADS SOLUCIONES SL se asegurará de que cualquier persona autorizada para procesar los datos esté bajo la obligación apropiada de confidencialidad. En caso de incidente de seguridad, se notificará sin demoras indebidas.</p>
            </section>

            <section className="p-5 bg-purple-50 rounded-xl border border-purple-100">
              <h2 className="text-lg font-bold text-slate-900 mb-2">Aceptación, consentimiento y revocabilidad</h2>
              <p className="text-slate-600 text-sm">
                El usuario declara haber sido informado de las condiciones sobre protección de datos de carácter personal, aceptando y consintiendo el tratamiento de los mismos por parte de FASCINADS SOLUCIONES SL. El consentimiento prestado es <strong>revocable en cualquier momento</strong> comunicándolo a FASCINADS SOLUCIONES SL.
              </p>
              <p className="text-slate-600 text-sm mt-2">
                FASCINADS SOLUCIONES SL se reserva el derecho a modificar la presente política para adaptarla a novedades legislativas. De acuerdo con la LSSICE, no realizamos prácticas de SPAM y nos comprometemos a no enviar comunicaciones comerciales sin identificarlas debidamente.
              </p>
            </section>

          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 py-8 mt-8">
        <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-600">
          <p>&copy; 2026 SkiFlow Rental · FASCINADS SOLUCIONES SL · B70880182</p>
          <div className="flex gap-4">
            <a href="/politica-privacidad" className="hover:text-slate-600 transition-colors">Privacidad</a>
            <a href="/politica-cookies" className="hover:text-slate-600 transition-colors">Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
